/**
 * Stable composite key for plan selection: `"<exact-id>::<plan.type>"`.
 *
 * Solves the transient batch_id collision between the NPV-optimized and
 * Pukkala-AAF plans that share the same batch UUID as their Plan.id until the
 * optimized plan's createSavedPlan POST resolves.
 *
 * Persisted plans use their row id. Unsaved generated futures use their opaque
 * generation reference so several lenses from one batch remain distinct.
 */

import type { PlanType } from '@/types/operations-planning';

/** Encode `(id, type)` into a stable composite key `"id::type"`. */
export function encodePlanKey(id: string, type: PlanType): string {
  return `${id}::${type}`;
}

/** Decode a composite key back to its `(id, type)` components.
 *  When the key contains no `"::"` separator (a legacy or bare id),
 *  `type` is returned as `null` and `id` equals the full input. */
export function decodePlanKey(key: string): { id: string; type: PlanType | null } {
  const sep = key.lastIndexOf('::');
  if (sep === -1) return { id: key, type: null };
  const typeStr = key.slice(sep + 2);
  return { id: key.slice(0, sep), type: (typeStr as PlanType) || null };
}

/**
 * Encode the exact plan identity. Unsaved lens futures share a simulation
 * batch, so their opaque generation reference—not the batch id—distinguishes
 * them until one is persisted.
 */
export function planKey(plan: {
  id: string;
  type: PlanType;
  savedPlanId?: string | null;
  generatedPlanRef?: string | null;
}): string {
  return encodePlanKey(plan.savedPlanId ?? plan.generatedPlanRef ?? plan.id, plan.type);
}
